import React from 'react';
import { useTranslation } from 'react-i18next';
import {
  FileText,
  Download,
  BookOpen,
  HelpCircle,
  Users,
  Map,
  Droplets,
  Calendar,
  AlertTriangle,
  Settings,
  Shield,
  Wrench,
  Play,
  Video
} from 'lucide-react';

const Documentation: React.FC = () => {
  const { t } = useTranslation('documentation');

  const chapters = [
    { id: 1, title: t('chapters.introduction.title'), icon: BookOpen, description: t('chapters.introduction.description') },
    { id: 2, title: t('chapters.firstConnection.title'), icon: Users, description: t('chapters.firstConnection.description') },
    { id: 3, title: t('chapters.dashboard.title'), icon: FileText, description: t('chapters.dashboard.description') },
    { id: 4, title: t('chapters.interactiveMap.title'), icon: Map, description: t('chapters.interactiveMap.description') },
    { id: 5, title: t('chapters.zoneManagement.title'), icon: Droplets, description: t('chapters.zoneManagement.description') },
    { id: 6, title: t('chapters.iotDevices.title'), icon: Wrench, description: t('chapters.iotDevices.description') },
    { id: 7, title: t('chapters.alertSystem.title'), icon: AlertTriangle, description: t('chapters.alertSystem.description') },
    { id: 8, title: t('chapters.planning.title'), icon: Calendar, description: t('chapters.planning.description') },
    { id: 9, title: t('chapters.workOrders.title'), icon: FileText, description: t('chapters.workOrders.description') },
    { id: 10, title: t('chapters.settings.title'), icon: Settings, description: t('chapters.settings.description') },
    { id: 11, title: t('chapters.administration.title'), icon: Shield, description: t('chapters.administration.description') },
    { id: 12, title: t('chapters.faq.title'), icon: HelpCircle, description: t('chapters.faq.description') },
  ];

  const handleDownloadPDF = () => {
    window.open('/docs/Guide-Utilisateur-AgriSafe.pdf', '_blank');
  };

  const handleDownloadMarkdown = () => {
    window.open('/docs/guide-utilisateur-agrisafe.md', '_blank');
  };

  const handleDownloadVideo = () => {
    window.open('/docs/video/AgriSafe-Walkthrough.mp4', '_blank');
  };

  const handlePlayVideo = () => {
    const modal = document.createElement('div');
    modal.id = 'video-modal';
    modal.style.cssText = `
      position: fixed;
      top: 0;
      left: 0;
      width: 100%;
      height: 100%;
      background: rgba(0,0,0,0.9);
      display: flex;
      align-items: center;
      justify-content: center;
      z-index: 9999;
    `;
    modal.innerHTML = `
      <div style="position: relative; width: 90%; max-width: 1200px;">
        <button onclick="document.getElementById('video-modal').remove()"
          style="position: absolute; top: -40px; right: 0; background: white; border: none;
          width: 36px; height: 36px; border-radius: 50%; cursor: pointer; font-size: 20px;">
          ✕
        </button>
        <video controls autoplay style="width: 100%; border-radius: 8px;">
          <source src="/docs/video/AgriSafe-Walkthrough.mp4" type="video/mp4">
        </video>
      </div>
    `;
    modal.onclick = (e) => {
      if (e.target === modal) modal.remove();
    };
    document.body.appendChild(modal);
  };

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4">
        <div>
          <h1 className="text-heading-2 text-[var(--text-primary)]">📘 {t('title')}</h1>
          <p className="text-body text-[var(--text-secondary)] mt-1">
            {t('subtitle')}
          </p>
        </div>

        <div className="flex flex-wrap gap-2">
          <button
            onClick={handleDownloadPDF}
            className="btn-primary flex items-center space-x-2"
            data-testid="download-pdf"
          >
            <Download className="w-4 h-4" />
            <span>{t('pdf')}</span>
          </button>
          <button
            onClick={handleDownloadMarkdown}
            className="btn-secondary flex items-center space-x-2"
            data-testid="download-md"
          >
            <FileText className="w-4 h-4" />
            <span>{t('text')}</span>
          </button>
          <button
            onClick={handlePlayVideo}
            className="flex items-center space-x-2 px-4 py-2 bg-[var(--color-danger)] text-white rounded-lg hover:opacity-90 transition-opacity"
            data-testid="play-video"
          >
            <Play className="w-4 h-4" />
            <span>{t('video')}</span>
          </button>
        </div>
      </div>

      {/* Video Walkthrough Banner */}
      <div className="card" style={{ background: 'linear-gradient(135deg, #1a1a2e 0%, #16213e 100%)' }}>
        <div className="flex flex-col md:flex-row items-center justify-between gap-4 text-white">
          <div className="flex items-center space-x-4">
            <div className="w-16 h-16 bg-[var(--color-danger)] rounded-xl flex items-center justify-center">
              <Video className="w-8 h-8" />
            </div>
            <div>
              <h2 className="text-xl font-bold">🎬 {t('videoWalkthrough')}</h2>
              <p className="text-white text-opacity-80">
                {t('videoWalkthroughDesc')} • {t('durationMinutes', { count: 2 })}
              </p>
            </div>
          </div>
          <div className="flex gap-3">
            <button
              onClick={handlePlayVideo}
              className="flex items-center space-x-2 px-6 py-3 bg-[var(--color-danger)] text-white font-semibold rounded-lg hover:opacity-90 transition-opacity"
            >
              <Play className="w-5 h-5" />
              <span>{t('watch')}</span>
            </button>
            <button
              onClick={handleDownloadVideo}
              className="flex items-center space-x-2 px-6 py-3 bg-white bg-opacity-20 text-white font-semibold rounded-lg hover:bg-opacity-30 transition-colors"
            >
              <Download className="w-5 h-5" />
              <span>{t('downloadMp4')}</span>
            </button>
          </div>
        </div>
      </div>

      {/* Quick Stats */}
      <div className="grid grid-cols-2 md:grid-cols-5 gap-4">
        {[
          { label: t('chaptersCount'), value: '12', color: 'var(--color-primary)' },
          { label: t('pages'), value: '28', color: 'var(--color-info)' },
          { label: t('images'), value: '5', color: 'var(--color-warning)' },
          { label: t('video'), value: t('durationMinutes', { count: 2 }), color: 'var(--color-danger)' },
          { label: t('version'), value: '1.0', color: 'var(--color-success)' },
        ].map((stat, index) => (
          <div key={index} className="card text-center">
            <div
              className="text-2xl font-bold mb-1"
              style={{ color: stat.color }}
            >
              {stat.value}
            </div>
            <div className="text-sm text-[var(--text-secondary)]">{stat.label}</div>
          </div>
        ))}
      </div>

      {/* Download Banner */}
      <div className="card bg-gradient-primary text-white">
        <div className="flex flex-col md:flex-row items-center justify-between gap-4">
          <div className="flex items-center space-x-4">
            <div className="w-16 h-16 bg-white bg-opacity-20 rounded-xl flex items-center justify-center">
              <BookOpen className="w-8 h-8" />
            </div>
            <div>
              <h2 className="text-xl font-bold">{t('completeGuide')}</h2>
              <p className="text-white text-opacity-90">
                {t('pageCount', { count: 24 })} • {t('completeGuideDesc')}
              </p>
            </div>
          </div>
          <button
            onClick={handleDownloadPDF}
            className="flex items-center space-x-2 px-6 py-3 bg-white text-[var(--color-primary)] font-semibold rounded-lg hover:bg-opacity-90 transition-colors"
          >
            <Download className="w-5 h-5" />
            <span>{t('downloadPdf')}</span>
          </button>
        </div>
      </div>

      {/* Chapters Grid */}
      <div>
        <h2 className="text-heading-3 text-[var(--text-primary)] mb-4">{t('tableOfContents')}</h2>
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
          {chapters.map((chapter) => {
            const Icon = chapter.icon;
            return (
              <div
                key={chapter.id}
                className="card hover:shadow-lg transition-all cursor-pointer"
                data-testid={`chapter-${chapter.id}`}
              >
                <div className="flex items-start space-x-4">
                  <div
                    className="w-10 h-10 rounded-lg flex items-center justify-center"
                    style={{ backgroundColor: 'var(--color-primary-100)' }}
                  >
                    <Icon className="w-5 h-5 text-[var(--color-primary)]" />
                  </div>
                  <div className="flex-1">
                    <div className="flex items-center space-x-2 mb-1">
                      <span className="text-xs font-medium text-[var(--text-tertiary)]">
                        {t('chapter')} {chapter.id}
                      </span>
                    </div>
                    <h3 className="text-heading-4 text-[var(--text-primary)] mb-1">
                      {chapter.title}
                    </h3>
                    <p className="text-body-sm text-[var(--text-secondary)]">
                      {chapter.description}
                    </p>
                  </div>
                </div>
              </div>
            );
          })}
        </div>
      </div>

      {/* Quick Reference */}
      <div className="card">
        <h2 className="text-heading-3 text-[var(--text-primary)] mb-4">🔐 {t('loginCredentials')}</h2>
        <div className="overflow-x-auto">
          <table className="w-full">
            <thead>
              <tr className="border-b border-[var(--border-light)]">
                <th className="text-left py-3 px-4 text-sm font-medium text-[var(--text-primary)]">{t('accountType')}</th>
                <th className="text-left py-3 px-4 text-sm font-medium text-[var(--text-primary)]">{t('email')}</th>
                <th className="text-left py-3 px-4 text-sm font-medium text-[var(--text-primary)]">{t('password')}</th>
                <th className="text-left py-3 px-4 text-sm font-medium text-[var(--text-primary)]">{t('access')}</th>
              </tr>
            </thead>
            <tbody>
              <tr className="border-b border-[var(--border-light)] hover:bg-[var(--gray-50)]">
                <td className="py-3 px-4">
                  <span className="px-2 py-1 text-xs bg-[var(--color-danger)] text-white rounded-full">
                    {t('superAdmin')}
                  </span>
                </td>
                <td className="py-3 px-4 font-mono text-sm">admin@agrisafe.fr</td>
                <td className="py-3 px-4 font-mono text-sm">admin123</td>
                <td className="py-3 px-4 text-sm text-[var(--text-secondary)]">{t('fullAccess')}</td>
              </tr>
              <tr className="border-b border-[var(--border-light)] hover:bg-[var(--gray-50)]">
                <td className="py-3 px-4">
                  <span className="px-2 py-1 text-xs bg-[var(--color-primary)] text-white rounded-full">
                    {t('adminCoop')}
                  </span>
                </td>
                <td className="py-3 px-4 font-mono text-sm">coop@agrisafe.fr</td>
                <td className="py-3 px-4 font-mono text-sm">coop123</td>
                <td className="py-3 px-4 text-sm text-[var(--text-secondary)]">{t('orgManagement')}</td>
              </tr>
              <tr className="border-b border-[var(--border-light)] hover:bg-[var(--gray-50)]">
                <td className="py-3 px-4">
                  <span className="px-2 py-1 text-xs bg-[var(--color-info)] text-white rounded-full">
                    {t('farmer')}
                  </span>
                </td>
                <td className="py-3 px-4 font-mono text-sm">farmer@agrisafe.fr</td>
                <td className="py-3 px-4 font-mono text-sm">farmer123</td>
                <td className="py-3 px-4 text-sm text-[var(--text-secondary)]">{t('basicAccess')}</td>
              </tr>
              <tr className="hover:bg-[var(--gray-50)]">
                <td className="py-3 px-4">
                  <span className="px-2 py-1 text-xs bg-[var(--color-warning)] text-white rounded-full">
                    {t('technician')}
                  </span>
                </td>
                <td className="py-3 px-4 font-mono text-sm">tech@agrisafe.fr</td>
                <td className="py-3 px-4 font-mono text-sm">tech123</td>
                <td className="py-3 px-4 text-sm text-[var(--text-secondary)]">{t('maintenanceAccess')}</td>
              </tr>
            </tbody>
          </table>
        </div>
      </div>

      {/* Support Section */}
      <div className="card bg-[var(--gray-50)]">
        <div className="flex items-start space-x-4">
          <div className="w-12 h-12 bg-[var(--color-info-100)] rounded-lg flex items-center justify-center">
            <HelpCircle className="w-6 h-6 text-[var(--color-info)]" />
          </div>
          <div>
            <h3 className="text-heading-4 text-[var(--text-primary)] mb-2">{t('needHelp')}</h3>
            <p className="text-body-sm text-[var(--text-secondary)] mb-3">
              {t('supportMessage')}
            </p>
            <div className="flex flex-wrap gap-4 text-sm">
              <span className="text-[var(--text-secondary)]">
                📧 support@agrisafe.fr
              </span>
              <span className="text-[var(--text-secondary)]">
                📞 +33 4 67 XX XX XX
              </span>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default Documentation;
